import type { SheetRow, SheetColumn } from "../../engine/models";
import type { SheetCell } from "../../engine/cells";
import type { Id } from "../../engine/models";
import type { ApiLineItem, ApiLineGroup } from "./types";
import { generateCellForLineItem } from "./cellMappers";

export function lineItemToSheetRow(
    item: ApiLineItem,
    columns: SheetColumn[],
    parentId?: Id,
    indentLevel: number = 0,
): SheetRow {
    const cells: SheetCell[] = columns.map((c) =>
        generateCellForLineItem(
            item,
            c.columnId as string,
            parentId,
            indentLevel,
        ),
    );
    return {
        rowId: `item-${item.id}`,
        parentId,
        indentLevel,
        reorderable: true,
        cells,
        metadata: { type: "line_item", sourceId: item.id },
    };
}

export function lineGroupToSheetRows(
    group: ApiLineGroup,
    columns: SheetColumn[],
    parentId?: Id,
    indentLevel: number = 0,
): SheetRow[] {
    const rows: SheetRow[] = [];
    const groupId = `group-${group.id}`;
    const isRoot = indentLevel === 0;

    const headerRowCells: SheetCell[] = columns.map((c) => {
        const isRunning = (c.columnId as string).startsWith(
            "line_item_running.",
        );
        const baseId = isRunning
            ? (c.columnId as string).replace("line_item_running.", "")
            : (c.columnId as string);

        if (baseId === "_order") {
            return {
                type: "statusDot",
                text: "☰",
                nonEditable: true,
                style: {
                    cursor: "grab",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                },
            };
        }
        if (baseId === "custom_id") {
            return { type: "header", text: "", nonEditable: true };
        }
        if (baseId === "name") {
            return {
                type: "chevron",
                text: group.name,
                hasChildren: true,
                isExpanded: true,
                parentId,
                indentLevel,
                isDeletable: true,
                canAddAddon: true,
                hasSettings: true,
                style: {
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                },
            };
        }
        if (baseId === "client_budget" || baseId === "client_budget_buffered") {
            return {
                type: "number",
                value: group.client_budget || 0,
                nonEditable: true,
                style: { fontWeight: "bold" },
            };
        }
        if (baseId === "client_quoted" || baseId === "client_quoted_original") {
            return {
                type: "number",
                value: group.client_quoted || 0,
                nonEditable: true,
                style: { fontWeight: "bold" },
            };
        }
        if (baseId === "variance") {
            return {
                type: "number",
                value: group.variance || 0,
                nonEditable: true,
                style: { fontWeight: "bold" },
            };
        }
        if (baseId === "client_budget_buffered_original") {
            return {
                type: "number",
                value: group.client_budget_buffered_original || 0,
                nonEditable: true,
                style: { fontWeight: "bold" },
            };
        }
        return { type: "header", text: "" };
    });

    rows.push({
        rowId: groupId,
        parentId,
        indentLevel,
        isExpanded: true,
        reorderable: true,
        cssClass: isRoot ? "section-row" : "category-row",
        metadata: { type: "line_group", sourceId: group.id },
        cells: headerRowCells,
    });

    if (group.line_items) {
        group.line_items.forEach((item) => {
            rows.push(
                lineItemToSheetRow(item, columns, groupId, indentLevel + 1),
            );
        });
    }

    if (group.addons && group.addons.length > 0) {
        group.addons.forEach((addon: any) => {
            const addonCells: SheetCell[] = columns.map((c) => {
                const isRunning = (c.columnId as string).startsWith(
                    "line_item_running.",
                );
                const baseId = isRunning
                    ? (c.columnId as string).replace("line_item_running.", "")
                    : (c.columnId as string);

                if (baseId === "_order") {
                    return {
                        type: "statusDot",
                        text: "☰",
                        nonEditable: true,
                        style: {
                            cursor: "grab",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        },
                    };
                }
                if (baseId === "custom_id") {
                    return { type: "header", text: "", nonEditable: true };
                }
                if (baseId === "name") {
                    return {
                        type: "chevron",
                        text: `\u21B3 Add-on: ${addon.name} (${addon.type === "percentage" ? addon.amount + "%" : "$" + addon.amount})`,
                        isDeletable: true,
                        canAddAddon: true,
                        parentId: groupId,
                        indentLevel: indentLevel + 1,
                        hasChildren: false,
                        isExpanded: false,
                        nonEditable: true,
                        style: { fontStyle: "italic" },
                    };
                }
                if (
                    baseId === "client_quoted" ||
                    baseId === "client_quoted_original"
                ) {
                    return {
                        type: "number",
                        value: addon.client_quoted || 0,
                        nonEditable: true,
                        style: { fontWeight: "bold" },
                    };
                }
                return { type: "text", text: "", nonEditable: true };
            });

            rows.push({
                rowId: `addon-${addon.id}`,
                parentId: groupId,
                indentLevel: indentLevel + 1,
                isExpanded: false,
                reorderable: false,
                cssClass: "addon-row",
                metadata: { type: "addon", sourceId: addon.id },
                cells: addonCells,
            });
        });
    }

    if (group.children) {
        group.children.forEach((childGroup) => {
            rows.push(
                ...lineGroupToSheetRows(
                    childGroup,
                    columns,
                    groupId,
                    indentLevel + 1,
                ),
            );
        });
    }

    const actionRowCells: SheetCell[] = columns.map((c, i) => {
        const isRunning = (c.columnId as string).startsWith(
            "line_item_running.",
        );
        const baseId = isRunning
            ? (c.columnId as string).replace("line_item_running.", "")
            : (c.columnId as string);

        if (baseId === "name") {
            return {
                type: "addButton",
                parentMetadata: group,
                rowType: group.parent_id == null ? "category" : "line",
                indentLevel: indentLevel + 1,
                text: "",
                value: 0,
            };
        }
        return { type: "header", text: "" };
    });

    rows.push({
        rowId: `action-group-${group.id}`,
        parentId: groupId,
        height: 32,
        indentLevel: indentLevel + 1,
        cells: actionRowCells,
    });

    return rows;
}
